import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Crown, Shield, Edit3, User } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown, color: "bg-amber-50 text-amber-700" },
  admin: { label: "Admin", icon: Shield, color: "bg-wine-50 text-wine-700" },
  editor: { label: "Editor", icon: Edit3, color: "bg-blue-50 text-blue-700" },
  member: { label: "Member", icon: User, color: "bg-neutral-100 text-neutral-700" },
};

export function MemberTeamBody() {
  const { t } = useLang();
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role_function: "", company_role: "member" });
  const [inviteResult, setInviteResult] = useState(null);

  const canManage = !!user?.company_id || user?.role === "admin";

  const load = useCallback(() => {
    if (user?.company_id) {
      api.get(`/companies/${user.company_id}/collaborators`).then((r) => setTeam(r.data)).catch(() => {});
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/companies/${user.company_id}/collaborators`, form);
      setInviteResult(data);
      setForm({ email: "", first_name: "", last_name: "", role_function: "", company_role: "member" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/collaborators/${u.id}`, { company_role: role }); toast.success("Rôle mis à jour"); load(); } catch { toast.error("Erreur"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Retirer ${u.name} de l'équipe ?`)) return;
    try { await api.delete(`/collaborators/${u.id}`); toast.success("Membre retiré"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!user?.company_id) return <div className="py-20 text-center">{t("portal_memberteam_01")}</div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">{t("portal_memberteam_02")}</h2>
          <p className="mt-1 text-sm text-neutral-600">{t("portal_memberteam_03")}</p>
        </div>
        {canManage && (
          <button onClick={() => { setOpen(!open); setInviteResult(null); }} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="invite-btn">
            {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Fermer" : "Inviter un collaborateur"}
          </button>
        )}
      </div>

      {open && canManage && (
        <div className="mt-6 rounded-2xl border border-wine-200 bg-white p-6">
          {inviteResult ? (
            <div className="rounded-xl bg-emerald-50 p-4">
              <h3 className="font-bold text-emerald-700">{t("portal_memberteam_04")}</h3>
              <p className="mt-2 text-sm text-emerald-800">{inviteResult.user.name} ({inviteResult.user.email}) a été ajouté avec le rôle <strong>{inviteResult.user.company_role}</strong>.</p>
              {inviteResult.email_sent ? (
                <p className="mt-3 text-sm text-emerald-700" data-testid="invite-email-sent">📧 Un email d&apos;invitation a été envoyé à <strong>{inviteResult.user.email}</strong> avec ses identifiants de connexion.</p>
              ) : (
                <>
                  <p className="mt-3 text-xs text-emerald-700"><strong>{t("portal_memberteam_05")}</strong>{t("portal_memberteam_06")}</p>
                  <div className="mt-1 font-mono text-sm font-bold text-emerald-900 bg-white px-3 py-2 rounded" data-testid="invite-temp-password">{inviteResult.invite_token_mocked}</div>
                </>
              )}
              <button onClick={() => setInviteResult(null)} className="mt-3 text-sm font-semibold text-emerald-700 underline" data-testid="invite-new-btn">{t("portal_memberteam_07")}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
              <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={t("portal_memberteam_90")} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="invite-first-name" />
              <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="invite-last-name" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email professionnel" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="invite-email" />
              <input value={form.role_function} onChange={(e) => setForm({ ...form, role_function: e.target.value })} placeholder="Fonction (ex: CMO, Data Scientist)" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="invite-function" />
              <select value={form.company_role} onChange={(e) => setForm({ ...form, company_role: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="invite-company-role">
                {Object.entries(ROLE_META).map(([k, v]) => k !== "owner" && <option key={k} value={k}>{v.label}</option>)}
              </select>
              <div className="md:col-span-2"><button type="submit" className="rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="invite-submit">{t("portal_memberteam_91")}</button></div>
            </form>
          )}
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="px-5 py-3 text-left">Personne</th><th className="px-5 py-3 text-left">Email</th><th className="px-5 py-3 text-left">Fonction</th><th className="px-5 py-3 text-left">{t("portal_memberteam_08")}</th>{canManage && <th className="px-5 py-3 text-right">Actions</th>}</tr>
          </thead>
          <tbody>
            {team.map((u) => {
              const role = u.company_role || "member";
              const M = ROLE_META[role] || ROLE_META.member;
              const initials = `${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`;
              return (
                <tr key={u.id} className="border-t border-neutral-100" data-testid={`team-row-${u.id}`}>
                  <td className="px-5 py-4"><div className="flex items-center gap-3"><div className="grid h-9 w-9 place-items-center rounded-full bg-wine-100 text-sm font-bold text-wine-700">{initials}</div><div><div className="font-semibold text-neutral-900">{u.name}</div>{u.invite_pending && <div className="text-[11px] text-amber-700">Invitation en attente</div>}</div></div></td>
                  <td className="px-5 py-4 text-neutral-700">{u.email}</td>
                  <td className="px-5 py-4 text-neutral-700">{u.role_function || "—"}</td>
                  <td className="px-5 py-4"><span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${M.color}`}><M.icon className="h-3 w-3" /> {M.label}</span></td>
                  {canManage && (
                    <td className="px-5 py-4 text-right">
                      {role !== "owner" && u.id !== user?.id && (
                        <div className="inline-flex items-center gap-2">
                          <select value={role} onChange={(e) => changeRole(u, e.target.value)} className="rounded-full border border-neutral-200 bg-white px-2 py-1 text-xs" data-testid={`team-role-${u.id}`}>
                            {Object.entries(ROLE_META).map(([k, v]) => k !== "owner" && <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          <button onClick={() => remove(u)} className="text-red-500 hover:text-red-700" data-testid={`team-remove-${u.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                      {u.id === user?.id && <span className="text-xs italic text-neutral-400">Vous</span>}
                    </td>
                  )}
                </tr>
              );
            })}
            {team.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-neutral-500">{t("portal_memberteam_92")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MemberTeam() {
  return (
    <PortalLayout sidebar="member">
      <MemberTeamBody />
    </PortalLayout>
  );
}
